import type { FC, ReactNode } from 'react'
import type { Highlight, Profile } from '@/types'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { SectionLabel } from '@/components/generals/section-label'
import { Star } from '@/components/generals/star'
import { CountUp } from '@/components/motion/count-up'
import { InkReveal } from '@/components/motion/ink-reveal'
import { Reveal } from '@/components/motion/reveal'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import { getRevealProps } from '@/constants/motion'

interface AboutSectionProps {
    profile: Profile
    highlights: Highlight[]
}

const TILE_STYLES = [
    'bg-secondary text-secondary-foreground',
    'bg-primary text-primary-foreground',
    'bg-accent text-accent-foreground'
]

export const AboutSection: FC<AboutSectionProps> = ({ profile, highlights }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const reduced = usePrefersReducedMotion()
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] })
    const starRotate = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [-60, 200])
    const starY = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [80, -120])

    return (
        <section id='about' ref={sectionRef} className='relative overflow-hidden py-20 md:py-32'>
            <motion.div
                aria-hidden='true'
                style={{ rotate: starRotate, y: starY }}
                className='pointer-events-none absolute -right-16 top-10 size-48 text-accent md:right-8 md:size-72'
            >
                <Star className='size-full' />
            </motion.div>

            <Container className='relative'>
                <Reveal>
                    <SectionLabel index='01' label='About me' />
                </Reveal>

                {profile.bio && (
                    <div className='mt-10 max-w-5xl md:mt-14'>
                        <InkReveal
                            text={profile.bio}
                            cms={`profile:${profile.id}:bio`}
                            className='font-heading text-[clamp(1.75rem,3.6vw,3.4rem)] font-semibold leading-[1.12] tracking-[-0.02em]'
                        />
                    </div>
                )}

                {highlights.length > 0 && (
                    <ul className='mt-16 grid gap-5 sm:grid-cols-2 md:mt-24 lg:grid-cols-3'>
                        {highlights.map((highlight, index) => (
                            <motion.li
                                key={highlight.id}
                                {...getRevealProps(index * 0.12, 60)}
                                className={cn('flex min-h-52 flex-col justify-between rounded-[2rem] p-7 md:p-9', TILE_STYLES[index % TILE_STYLES.length])}
                            >
                                <p className='font-heading text-6xl font-extrabold leading-none tracking-[-0.04em] md:text-7xl'>
                                    {highlight.prefix && (
                                        <span data-mdlfy-cms={`highlights:${highlight.id}:prefix`}>{highlight.prefix}</span>
                                    )}

                                    <CountUp value={highlight.value} />

                                    {highlight.suffix && (
                                        <span data-mdlfy-cms={`highlights:${highlight.id}:suffix`}>{highlight.suffix}</span>
                                    )}
                                </p>

                                <p className='mt-6 font-mono text-xs font-bold uppercase tracking-[0.16em]'>
                                    <span data-mdlfy-cms={`highlights:${highlight.id}:label`}>{highlight.label}</span>
                                </p>
                            </motion.li>
                        ))}
                    </ul>
                )}
            </Container>
        </section>
    )
}
